<template>
    <section class="device-scanner">
        <div class="scanner-input-row">
            <van-field
                v-model.trim="manualCode"
                class="scanner-field"
                clearable
                label="设备码"
                placeholder="输入内部 SN / 原厂 SN"
                maxlength="128"
                @keyup.enter="submitManual"
            >
                <template #button>
                    <van-button size="small" type="primary" :disabled="!manualCode" @click="submitManual">查询</van-button>
                </template>
            </van-field>
            <van-button class="camera-button" type="primary" icon="scan" :loading="scanning" @click="openScanner">扫码</van-button>
        </div>

        <div v-if="scanning && !nativeScanning" class="camera-panel">
            <div :id="readerId" class="camera-reader"></div>
            <div class="camera-mask">
                <div class="scan-frame"><i></i><i></i><i></i><i></i></div>
                <p>将二维码或条形码放入框内</p>
            </div>
            <van-button plain block type="default" @click="stopScanner">取消扫码</van-button>
        </div>

        <div v-else-if="scanning && nativeScanning" class="native-scan-tip">
            <van-icon name="scan" />
            <strong>正在使用手机摄像头扫码</strong>
            <span>将设备标签放入取景框，识别后会自动返回</span>
            <van-button plain type="default" @click="stopScanner">取消扫码</van-button>
        </div>

        <p v-if="hint" class="scanner-hint"><van-icon name="info-o" />{{ hint }}</p>
    </section>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'
import { Capacitor } from '@capacitor/core'
import { BarcodeScanner } from '@capacitor-community/barcode-scanner'

let scannerSeed = 0

export default {
    name: 'DeviceScanner',
    props: {
        hint: {
            type: String,
            default: '支持二维码、Code 128 条形码、扫码枪和手工输入'
        }
    },
    data() {
        scannerSeed += 1
        return {
            manualCode: '',
            scanning: false,
            nativeScanning: false,
            html5QrCode: null,
            lastResult: '',
            lastResultAt: 0,
            readerId: `device-scanner-reader-${scannerSeed}`
        }
    },
    computed: {
        isNativeApp() {
            return Capacitor && typeof Capacitor.isNativePlatform === 'function' && Capacitor.isNativePlatform()
        }
    },
    beforeDestroy() {
        this.stopScanner()
    },
    methods: {
        submitManual() {
            const value = this.normalizeCode(this.manualCode)
            if (!value) return
            this.emitCode(value)
        },

        async openScanner() {
            if (this.scanning) return
            if (this.isNativeApp) {
                await this.openNativeScanner()
            } else {
                await this.openBrowserScanner()
            }
        },

        async openNativeScanner() {
            this.scanning = true
            this.nativeScanning = true
            try {
                const permission = await BarcodeScanner.checkPermission({ force: true })
                if (!permission || !permission.granted) {
                    throw new Error('没有获得摄像头权限')
                }
                await BarcodeScanner.hideBackground()
                const result = await BarcodeScanner.startScan({
                    cameraDirection: 'back',
                    targetedFormats: ['QR_CODE', 'CODE_128', 'CODE_39', 'EAN_13', 'EAN_8']
                })
                if (result && result.hasContent) this.emitCode(result.content)
            } catch (error) {
                this.$emit('error', error)
            } finally {
                try { await BarcodeScanner.showBackground() } catch (e) { /* noop */ }
                this.scanning = false
                this.nativeScanning = false
            }
        },

        async openBrowserScanner() {
            this.scanning = true
            await this.$nextTick()
            try {
                this.html5QrCode = new Html5Qrcode(this.readerId)
                await this.html5QrCode.start(
                    { facingMode: 'environment' },
                    { fps: 12, qrbox: { width: 250, height: 180 }, aspectRatio: 1.6 },
                    decodedText => this.emitCode(decodedText),
                    () => { /* scan errors are expected while searching */ }
                )
            } catch (error) {
                this.$emit('error', error)
                await this.stopScanner()
            }
        },

        async stopScanner() {
            if (this.isNativeApp && this.nativeScanning) {
                try { await BarcodeScanner.stopScan({ resolveScan: false }) } catch (e) { /* noop */ }
                try { await BarcodeScanner.showBackground() } catch (e) { /* noop */ }
            }
            if (this.html5QrCode) {
                try { await this.html5QrCode.stop() } catch (e) { /* noop */ }
                try { await this.html5QrCode.clear() } catch (e) { /* noop */ }
                this.html5QrCode = null
            }
            this.scanning = false
            this.nativeScanning = false
        },

        emitCode(rawCode) {
            const code = this.normalizeCode(rawCode)
            if (!code) return
            const now = Date.now()
            if (code === this.lastResult && now - this.lastResultAt < 1500) return
            this.lastResult = code
            this.lastResultAt = now
            this.manualCode = code
            this.stopScanner()
            this.$emit('scanned', code)
        },

        normalizeCode(rawCode) {
            let value = String(rawCode || '').trim()
            if (!value) return ''
            try {
                if (/^https?:\/\//i.test(value)) {
                    const url = new URL(value)
                    const readDeviceCode = params => {
                        const supported = ['internal_sn', 'device_sn', 'dsn', 'sn', 'code']
                        for (const [key, item] of params.entries()) {
                            if (supported.includes(String(key).toLowerCase()) && String(item).trim()) return item
                        }
                        return ''
                    }
                    let parsedCode = readDeviceCode(url.searchParams)
                    if (!parsedCode && url.hash) {
                        const hash = url.hash.replace(/^#\/?/, '')
                        const queryIndex = hash.indexOf('?')
                        const hashQuery = queryIndex >= 0 ? hash.slice(queryIndex + 1) : hash
                        parsedCode = readDeviceCode(new URLSearchParams(hashQuery))
                    }
                    value = parsedCode || value
                } else if (/^\{/.test(value)) {
                    const parsed = JSON.parse(value)
                    value = parsed.internal_sn || parsed.device_sn || parsed.dsn || parsed.sn || parsed.code || value
                }
            } catch (e) {
                // 非 URL 或非 JSON 时按原始编码处理
            }
            // 设备 SN 可能区分大小写，不能把纯 SN 或整条 URL 强制转成大写。
            return String(value).trim().replace(/[\r\n\t]/g, '')
        }
    }
}
</script>

<style scoped>
.device-scanner { width: 100%; }
.scanner-input-row { display: flex; align-items: stretch; gap: 12px; }
.scanner-field { flex: 1; min-width: 0; padding: 0; border: 1px solid #dce5f2; border-radius: 14px; overflow: hidden; background: #fbfcff; transition: border-color .2s ease, box-shadow .2s ease; }
.scanner-field:focus-within { border-color: #6f9cff; box-shadow: 0 0 0 3px rgba(49,95,232,.1); }
.scanner-field::after { display: none; }
.scanner-field ::v-deep .van-field__button { padding-right: 8px; }
.scanner-field ::v-deep .van-button { min-width: 58px; height: 44px; border-radius: 10px; font-size: 18px; }
.camera-button { flex: 0 0 108px; height: 76px; padding: 0 8px; border-radius: 14px; box-shadow: 0 8px 16px rgba(49,95,232,.18); white-space: nowrap; }
.camera-button ::v-deep .van-button__content { gap: 6px; }
.camera-button ::v-deep .van-button__text { white-space: nowrap; }
.camera-panel { position: relative; margin-top: 18px; padding: 18px; border-radius: 18px; background: #101827; overflow: hidden; }
.camera-reader { min-height: 380px; border-radius: 12px; overflow: hidden; background: #05070a; }
.camera-reader video { width: 100% !important; height: 380px !important; object-fit: cover !important; }
.camera-mask { position: absolute; inset: 18px 18px 86px; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; }
.camera-mask p { margin-top: 18px; color: #fff; font-size: 24px; text-shadow: 0 1px 4px rgba(0,0,0,.6); }
.scan-frame { position: relative; width: 78%; max-width: 360px; height: 180px; border: 1px solid rgba(255,255,255,.45); border-radius: 12px; }
.scan-frame i { position: absolute; width: 28px; height: 28px; border-color: #4e89ff; border-style: solid; }
.scan-frame i:nth-child(1) { top: -2px; left: -2px; border-width: 4px 0 0 4px; }
.scan-frame i:nth-child(2) { top: -2px; right: -2px; border-width: 4px 4px 0 0; }
.scan-frame i:nth-child(3) { bottom: -2px; left: -2px; border-width: 0 0 4px 4px; }
.scan-frame i:nth-child(4) { bottom: -2px; right: -2px; border-width: 0 4px 4px 0; }
.native-scan-tip { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 28px 16px; color: #4a5568; font-size: 24px; text-align: center; }
.native-scan-tip .van-icon { font-size: 60px; color: #4e89ff; }
.native-scan-tip span { color: #8994a6; font-size: 22px; }
.scanner-hint { display: flex; align-items: flex-start; gap: 8px; margin: 12px 2px 0; color: #7e8ca0; font-size: 20px; line-height: 1.5; }
.device-scanner ::v-deep .van-field__label { width: 84px; font-size: 21px; line-height: 1.5; }
.device-scanner ::v-deep .van-field__control { font-size: 21px; line-height: 1.55; }
.device-scanner ::v-deep .van-button { font-size: 20px; }
</style>
